import { inject, Injectable, signal, WritableSignal } from '@angular/core';

import { MOD_VISUALIZATION_BASEMAPS } from '../../domain/constants/basemaps.constant';
import { MOD_VISUALIZATION_OVERLAYS } from '../../domain/constants/overlays.constant';
import { Basemap, Legend, MapControl, Overlay } from '../../domain/types';
import { StorageRepositoryService } from './storage.repository.service';
import { MapLayersService } from './map-layers.service';
import { MapService } from './map.service';
import { MOD_COMMUNITY_LEGENDS } from '../../domain/constants/legends.constant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GEOSERVER_WORKSPACE } from '../../domain/constants/geoserver.constant';
import { FilterSignalService } from '../signals/filter-signals.service';
import { GEOSERVER_URL } from 'src/environments/environment.prod';

@Injectable({
  providedIn: 'root'
})
export class MapControlsService {

  private readonly mapService: MapService = inject(MapService)
  private readonly mapLayersService: MapLayersService = inject(MapLayersService)
  private readonly snackBar: MatSnackBar = inject(MatSnackBar)
  private readonly storageRepositoryService: StorageRepositoryService = inject(StorageRepositoryService)
  private readonly filterSignalService: FilterSignalService = inject(FilterSignalService)

  map: maplibregl.Map;
  activatedControl: WritableSignal<MapControl> = signal<MapControl>(undefined);

  overlays: WritableSignal<Overlay[]> = signal<Overlay[]>(
    this.restoreOverlaysFromStorage(
      this.storageRepositoryService.load('overlays', 'local', [])
    )
  );
  basemaps: WritableSignal<Basemap[]> = signal<Basemap[]>(
    this.restoreBasemapsFromStorage(
      this.storageRepositoryService.load('basemaps', 'local', [])
    )
  );
  legends: WritableSignal<Legend[]> = signal<Legend[]>(MOD_COMMUNITY_LEGENDS);

  pointInspectorInfo: WritableSignal<{ [key: string]: any }> = signal<{ [key: string]: any }>(undefined);
  pointInspectorHistory: WritableSignal<{ [key: string]: any }> = signal<{ [key: string]: any }>(undefined);
  pointInspectorFireHistory: WritableSignal<{ [key: string]: any }> = signal<{ [key: string]: any }>(undefined);

  pointInspectorData: WritableSignal<{
    info: { [key: string]: any };
    history: { [key: string]: any };
    fireHistory: { [key: string]: any };
  }> = signal({
    info: undefined,
    history: undefined,
    fireHistory: undefined
  });

  activatedControlChange(mapControl: MapControl) {
    this.activatedControl() === mapControl ? this.activatedControl.set(null) : this.activatedControl.set(mapControl);
  }

  /* Overlays Methods */
  addAlertsLayerToOverlays() {

    // Check if alerts layer already exists
    if (this.overlays().some(overlay => overlay.id === 'alerts')) {
      return;
    }

    const alertOverlays: Overlay[] = [
      {
        id: 'alerts',
        layers: `${GEOSERVER_WORKSPACE}:geoserver_communities`,
        name: 'Kits Contratados',
        source: 'Povos da Floresta',
        year: '2024',
        downloadUrl: `${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=geoserver_communities&outputFormat=SHAPE-ZIP&format_options=filename:Kits_Contratados.zip`,
        isVisible: this.filterSignalService.alertVisibility(),
      },
      ...this.overlays()
    ];
    this.updateOverlays(alertOverlays);
  }

  updateOverlays(updatedOverlays: Overlay[], overlaysIndex?: number) {
    // Sync the alerts visibility with the filter signal service
    const alertsOverlay = updatedOverlays.find(overlay => overlay.id === 'alerts');
    if (alertsOverlay) {
      this.filterSignalService.alertVisibility.set(alertsOverlay.isVisible);

      // // If enabling alerts, make sure to create layer with current filter
      // if (alertsOverlay.isVisible) {
      //     this.mapLayersService.createAlertsLayer(this.filterSignalService.filter());
      // }
    }

    this.overlays.set(updatedOverlays);

    // Store only serializable data
    const overlaysToStore = this.prepareOverlaysForStorage(updatedOverlays);
    this.storageRepositoryService.save('overlays', overlaysToStore, 'local');

    const arraySize = updatedOverlays.length;
    const invertedOverlays = updatedOverlays.slice().reverse();

    updatedOverlays.forEach((item, index) => {
      const mapIndex = arraySize - index;
      this.mapLayersService.updateOverlayItem(invertedOverlays[index], mapIndex);
    });
  }


  /* Basemaps Methods */
  updateBasemaps(updatedBasemaps: Basemap[], basemapIndex: number) {
    updatedBasemaps.forEach(basemap => basemap.isVisible = false);
    updatedBasemaps[basemapIndex].isVisible = true;
    this.basemaps.set(updatedBasemaps);

    // Store only serializable data
    const basemapsToStore = this.prepareBasemapsForStorage(updatedBasemaps);
    this.storageRepositoryService.save('basemaps', basemapsToStore, 'local');

    this.mapLayersService.updateBasemapLayer(updatedBasemaps[basemapIndex], basemapIndex);
  }

  /* Storage Methods */
  private prepareOverlaysForStorage(overlays: Overlay[]): Partial<Overlay>[] {
    return overlays.map(overlay => ({
      id: overlay.id,
      layers: overlay.layers,
      name: overlay.name,
      source: overlay.source,
      year: overlay.year,
      isVisible: overlay.isVisible,
      downloadUrl: overlay.downloadUrl,
      // subitens: overlay.subitens?.map(subitem => ({
      //   name: subitem.name,
      //   isVisible: subitem.isVisible,
      //   color: subitem.color
      // }))
    }));
  }

  private prepareBasemapsForStorage(basemaps: Basemap[]): Partial<Basemap>[] {
    return basemaps.map(basemap => ({
      name: basemap.name,
      isVisible: basemap.isVisible
    }));
  }

  private restoreOverlaysFromStorage(storedOverlays: Partial<Overlay>[]): Overlay[] {
    // Start with default overlays
    const defaultOverlays = MOD_VISUALIZATION_OVERLAYS;

    // Alerts overlay persistence
    if (storedOverlays && storedOverlays.length > 0) {
      const alertsOverlay = storedOverlays.find(overlay => overlay.id === 'alerts');
      if (alertsOverlay) {
        this.filterSignalService.alertVisibility.set(alertsOverlay.isVisible);
      }
    }

    // Merge stored visibility states with default overlays
    return defaultOverlays.map(defaultOverlay => {

      const storedOverlay = storedOverlays?.find(stored => stored.name === defaultOverlay.name);
      if (storedOverlay) {
        return {
          ...defaultOverlay,
          isVisible: storedOverlay.isVisible,
          subitens: defaultOverlay.subitens?.map(defaultSubitem => {
            const storedSubitem = storedOverlay.subitens?.find(
              stored => stored.name === defaultSubitem.name
            );
            return storedSubitem ? { ...defaultSubitem, isVisible: storedSubitem.isVisible } : defaultSubitem;
          })
        };
      }
      return defaultOverlay;
    });
  }

  private restoreBasemapsFromStorage(storedBasemaps: Partial<Basemap>[]): Basemap[] {
    const defaultBasemaps = MOD_VISUALIZATION_BASEMAPS;

    return defaultBasemaps.map(defaultBasemap => {
      const storedBasemap = storedBasemaps?.find(stored => stored.name === defaultBasemap.name);
      if (storedBasemap) {
        return {
          ...defaultBasemap,
          isVisible: storedBasemap.isVisible
        };
      }
      return defaultBasemap;
    })
  }
}
